import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
    Card, 
    Button, 
    Space, 
    Tag, 
    Form, 
    Input, 
    InputNumber,
    Select, 
    Typography,
    Descriptions,
    List,
    Avatar,
    Divider,
    Alert,
    Row,
    Col,
    Spin,
    Image,
    Modal,
    Upload,
    Grid,
    Breadcrumb,
    Result
} from 'antd';
import { 
    EyeOutlined, 
    CheckOutlined, 
    CloseOutlined, 
    UserOutlined,
    FileTextOutlined,
    PhoneOutlined,
    MailOutlined,
    DownloadOutlined,
    PrinterOutlined,
    ArrowLeftOutlined,
    EnvironmentOutlined,
    BankOutlined,
    CalendarOutlined,
    DollarOutlined,
    UploadOutlined,
    EditOutlined
} from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import { useReactToPrint } from 'react-to-print';
import { useSelfRegistration } from '../hooks/useSelfRegistration';
import { showSuccessNotification, showErrorNotification } from '@/components/common/utils/notify';
import {useGetSession} from "@/shared/api/client/self-registration";
import {useAuthUser} from "@/modules/auth/context/AuthContext";
import { AXIOS_INSTANCE } from '@/shared/api/mutator/custom-instance';
import SelfRegistrationDeductionForm from '../components/SelfRegistrationDeductionForm';
// import { HAZINA_DEDUCTION_FORM_CONFIG, DeductionFormConfig } from '@/modules/loans/components/DeductionFormConfig';
import { useCompany } from '@/hooks/company/use-company';
import { useGetActiveConfig } from '@/modules/onboarding/hooks/useSelfOnboardingConfig';
import { EmployerFormModal } from '@/modules/employers/components/EmployerFormModal';

// Stub types for missing loans module
interface DeductionFormConfig {
    header?: any;
    branding?: any;
    sections?: any;
}

const HAZINA_DEDUCTION_FORM_CONFIG: any = {};

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { useBreakpoint } = Grid;

const OfficerReviewDetailView: React.FC = () => {
    const { sessionToken } = useParams<{ sessionToken: string }>();
    const navigate = useNavigate();
    const screens = useBreakpoint();
    const isMobile = !screens.md;
    
    const [reviewForm] = Form.useForm();
    const [isReviewing, setIsReviewing] = useState(false);

    const [employerQuery, setEmployerQuery] = useState<string>('');
    const [employerOptions, setEmployerOptions] = useState<Array<{ label: string; value: number }>>([]);
    const [employerById, setEmployerById] = useState<Record<number, any>>({});
    const [isEmployerLoading, setIsEmployerLoading] = useState(false);
    const [selectedEmployerId, setSelectedEmployerId] = useState<number | undefined>(undefined);
    const [createEmployerModalOpen, setCreateEmployerModalOpen] = useState(false);
    const [employerEditModalOpen, setEmployerEditModalOpen] = useState(false);

    const [signedDeductionFormFileList, setSignedDeductionFormFileList] = useState<UploadFile[]>([]);
    const [officialDeductionFormFileList, setOfficialDeductionFormFileList] = useState<UploadFile[]>([]);

    const [deductionPreviewOpen, setDeductionPreviewOpen] = useState(false);
    const [isPrinting, setIsPrinting] = useState(false);
    const printableFormRef = useRef<HTMLDivElement>(null);
    const authUser = useAuthUser();

    // API hooks
    const { 
        approveSession,
        rejectSession,
        isApprovingSession,
        isRejectingSession,
        uploadDocument,
        isUploadingDocument
    } = useSelfRegistration();

    // Use the session hook directly
    const { 
        data: sessionData, 
        isLoading: loading, 
        error: sessionError,
        refetch: refetchSession
    } = useGetSession(sessionToken || '', {
        query: {
            enabled: !!sessionToken,
            refetchInterval: 30000,
            staleTime: 10000,
            select: (response: any) => response?.data?.data ?? response?.data ?? response
        }
    });

    const userId = authUser?.userId || '';

    const { data: companyData, isLoading: isCompanyLoading } = useCompany();
    const { data: activeOnboardingConfig } = useGetActiveConfig();

    const application = sessionData as any;

    const handlePrintDeductionForm = useReactToPrint({
        content: () => printableFormRef.current,
        onBeforePrint: () => {
            setIsPrinting(true);
            return new Promise<void>((resolve) => {
                setTimeout(resolve, 0);
            });
        },
        onAfterPrint: () => {
            setIsPrinting(false);
        },
        documentTitle: `Deduction_Form_${application?.fullName?.replace(/\s+/g, '_') || 'Applicant'}_${Date.now()}`,
        pageStyle: `
            @page {
                size: A4;
                margin: 10mm;
            }
            @media print {
                body {
                    -webkit-print-color-adjust: exact;
                    color-adjust: exact;
                }
                .ant-modal-mask, .ant-modal-wrap, .ant-btn {
                    display: none !important;
                }
            }
        `,
    });

    const computeInitials = (fullName?: string) => {
        const name = (fullName || '').trim();
        if (!name) {
            return '';
        }
        const parts = name.split(/\s+/).filter(Boolean);
        if (parts.length === 1) {
            return parts[0].substring(0, 1).toUpperCase();
        }
        const first = parts[0].substring(0, 1).toUpperCase();
        const last = parts[parts.length - 1].substring(0, 1).toUpperCase();
        return `${first}${last}`;
    };

    const handleUploadOfficialDeductionForm = async () => {
        if (!application?.sessionToken) {
            showErrorNotification('Missing session token');
            return;
        }

        if (officialDeductionFormFileList.length === 0) {
            showErrorNotification('Please select a file to upload');
            return;
        }

        const fileObj = officialDeductionFormFileList[0]?.originFileObj as File | undefined;
        if (!fileObj) {
            showErrorNotification('Invalid file');
            return;
        }

        try {
            await uploadDocument(application.sessionToken, 'OFFICIAL_DEDUCTION_FORM', fileObj);
            setOfficialDeductionFormFileList([]);
            await refetchSession();
            showSuccessNotification('Official deduction form uploaded');
        } catch (e: any) {
            showErrorNotification(e?.response?.data?.message || e?.message || 'Failed to upload official deduction form');
        }
    };

    const selectedEmployer = useMemo(() => {
        if (!selectedEmployerId) {
            return undefined;
        }
        return employerById[selectedEmployerId];
    }, [employerById, selectedEmployerId]);

    const deductionFormConfig: DeductionFormConfig = useMemo(() => {
        const raw = (activeOnboardingConfig as any)?.deductionFormConfig;
        if (raw) {
            try { return JSON.parse(raw); } catch {}
        }
        return HAZINA_DEDUCTION_FORM_CONFIG;
    }, [activeOnboardingConfig]);

    const requiresMemberSignedDeductionFormUpload = useMemo(() => {
        return (activeOnboardingConfig as any)?.requiresMemberSignedDeductionFormUpload !== false;
    }, [activeOnboardingConfig]);

    const resolvedVoteNumber = useMemo(() => {
        if (selectedEmployer?.voteNumber) return selectedEmployer.voteNumber as string;
        const fromNewEmployer = reviewForm.getFieldValue('newEmployerVoteNumber') as string | undefined;
        if (fromNewEmployer) return fromNewEmployer;
        return (application as any)?.voteNumber as string | undefined;
    }, [selectedEmployer, application]);

    const isDeductionFormReview = useMemo(() => {
        return application?.currentStep === 'DEDUCTION_FORM_REVIEW' || application?.status === 'PENDING_DEDUCTION_FORM_REVIEW';
    }, [application?.currentStep, application?.status]);

    const isDepositEvidenceReview = useMemo(() => {
        return application?.currentStep === 'DEPOSIT' && Boolean(application?.directDepositPendingReview);
    }, [application?.currentStep, application?.directDepositPendingReview]);

    const canReviewApplication = useMemo(() => {
        if (!application) {
            return false;
        }

        return application.status === 'PENDING_OFFICER_REVIEW'
            || application.status === 'PENDING_DEDUCTION_FORM_REVIEW'
            || isDeductionFormReview
            || isDepositEvidenceReview;
    }, [application, isDeductionFormReview, isDepositEvidenceReview]);

    const hasSignedDeductionForm = useMemo(() => {
        const docs = application?.uploadedDocuments || [];
        return docs.some((d: any) => (d?.documentType || '').toUpperCase() === 'SIGNED_DEDUCTION_FORM');
    }, [application?.uploadedDocuments]);

    const hasOfficialDeductionForm = useMemo(() => {
        const docs = application?.uploadedDocuments || [];
        return docs.some((d: any) => (d?.documentType || '').toUpperCase() === 'OFFICIAL_DEDUCTION_FORM');
    }, [application?.uploadedDocuments]);

    const mergedEmployerOptions = useMemo(() => {
        const selected = selectedEmployerId ? employerById[selectedEmployerId] : undefined;
        const selectedOption = selected
            ? [{
                value: selected.id,
                label: selected.voteNumber ? `${selected.name} (${selected.voteNumber})` : selected.name
            }]
            : [];
        const map = new Map<number, { label: string; value: number }>();
        employerOptions.forEach((o) => map.set(o.value, o));
        selectedOption.forEach((o) => map.set(o.value, o));
        return Array.from(map.values());
    }, [employerById, employerOptions, selectedEmployerId]);

    const refreshSelectedEmployer = async (employerId: number) => {
        const response = await AXIOS_INSTANCE.get(`/frontend/v1/employers/${employerId}`);
        const employer = response?.data?.data;
        if (employer?.id) {
            setEmployerById((prev) => ({ ...prev, [employer.id]: employer }));
            reviewForm.setFieldsValue({
                employerAddress: employer.address,
                employerPoBox: employer.poBox,
                employerPhone: employer.phone,
                employerEmail: employer.email,
                voteNumber: employer.voteNumber
            });
        }
    };

    useEffect(() => {
        if (!application) {
            return;
        }

        const fallbackOfficerName = authUser?.fullname || undefined;
        const fallbackOfficerPhone = authUser?.phoneNo || undefined;
        const fallbackOfficerInitials = computeInitials(authUser?.fullname);

        const currentOfficerName = reviewForm.getFieldValue('officerName');
        const currentOfficerPhone = reviewForm.getFieldValue('officerPhone');
        const currentOfficerInitials = reviewForm.getFieldValue('officerInitials');
        const currentMonthlyDeductionAmount = reviewForm.getFieldValue('monthlyDeductionAmount');
        const currentEmployerAddress = reviewForm.getFieldValue('employerAddress');
        const currentEmployerPoBox = reviewForm.getFieldValue('employerPoBox');
        const currentEmployerPhone = reviewForm.getFieldValue('employerPhone');
        const currentEmployerEmail = reviewForm.getFieldValue('employerEmail');
        const currentVoteNumber = reviewForm.getFieldValue('voteNumber');
        
        reviewForm.setFieldsValue({
            monthlyDeductionAmount: currentMonthlyDeductionAmount ?? application.monthlyDeductionAmount,
            officerName: currentOfficerName ?? (application as any)?.officerName ?? fallbackOfficerName,
            officerPhone: currentOfficerPhone ?? (application as any)?.officerPhone ?? fallbackOfficerPhone,
            officerInitials: currentOfficerInitials ?? (application as any)?.officerInitials ?? fallbackOfficerInitials,
            employerAddress: currentEmployerAddress ?? (application as any)?.employerAddress,
            employerPoBox: currentEmployerPoBox ?? (application as any)?.employerPoBox,
            employerPhone: currentEmployerPhone ?? (application as any)?.employerPhone,
            employerEmail: currentEmployerEmail ?? (application as any)?.employerEmail,
            voteNumber: currentVoteNumber ?? (application as any)?.voteNumber
        });

        const existingEmployerId = (application as any)?.employerId as number | undefined;
        if (!selectedEmployerId && existingEmployerId) {
            setSelectedEmployerId(existingEmployerId);
            refreshSelectedEmployer(existingEmployerId).catch(() => {
                // ignore
            });
        }
    }, [application, authUser, reviewForm, selectedEmployerId]);

    const handleUploadSignedDeductionForm = async () => {
        if (!application?.sessionToken) {
            showErrorNotification('Missing session token');
            return;
        }

        if (signedDeductionFormFileList.length === 0) {
            showErrorNotification('Please select a file to upload');
            return;
        }

        const fileObj = signedDeductionFormFileList[0]?.originFileObj as File | undefined;
        if (!fileObj) {
            showErrorNotification('Invalid file');
            return;
        }

        try {
            await uploadDocument(application.sessionToken, 'SIGNED_DEDUCTION_FORM', fileObj);
            setSignedDeductionFormFileList([]);
            await refetchSession();
            showSuccessNotification('Signed deduction form uploaded');
        } catch (e: any) {
            showErrorNotification(e?.response?.data?.message || e?.message || 'Failed to upload signed deduction form');
        }
    };

    useEffect(() => {
        if (!employerQuery || employerQuery.trim().length < 2) {
            setEmployerOptions([]);
            return;
        }

        const handle = setTimeout(async () => {
            setIsEmployerLoading(true);
            try {
                const response = await AXIOS_INSTANCE.get('/frontend/v1/employers', {
                    params: { q: employerQuery.trim(), limit: 10 }
                });

                const employers = response?.data?.data || [];
                const nextById: Record<number, any> = {};
                const nextOptions = (employers as any[]).map((e) => {
                    nextById[e.id] = e;
                    return {
                        label: e.voteNumber ? `${e.name} (${e.voteNumber})` : e.name,
                        value: e.id
                    };
                });

                setEmployerById((prev) => ({ ...prev, ...nextById }));
                setEmployerOptions(nextOptions);
            } catch (e) {
                console.error('Failed to search employers', e);
            } finally {
                setIsEmployerLoading(false);
            }
        }, 350);

        return () => clearTimeout(handle);
    }, [employerQuery]);

    useEffect(() => {
        if (sessionError) {
            showErrorNotification('Failed to load application details');
            console.error('Error loading application:', sessionError);
        }
    }, [sessionError]);

    const handleSubmitReview = async (values: any) => {
        if (!application) return;

        if (!canReviewApplication) {
            showErrorNotification('This application is not available for review');
            return;
        }

        try {
            setIsReviewing(true);

            const newEmployerName: string | undefined = values.newEmployerName;
            const newEmployerVoteNumber: string | undefined = values.newEmployerVoteNumber;
            const newEmployerAddress: string | undefined = values.newEmployerAddress;
            const newEmployerPoBox: string | undefined = values.newEmployerPoBox;
            const newEmployerPhone: string | undefined = values.newEmployerPhone;
            const newEmployerEmail: string | undefined = values.newEmployerEmail;

            const officerInitials: string | undefined = values.officerInitials;
            const officerName: string | undefined = values.officerName;
            const officerPhone: string | undefined = values.officerPhone;

            const monthlyDeductionAmount: number | undefined = values.monthlyDeductionAmount;

            const employerAddressOverride: string | undefined = values.employerAddress;
            const employerPoBoxOverride: string | undefined = values.employerPoBox;
            const employerPhoneOverride: string | undefined = values.employerPhone;
            const employerEmailOverride: string | undefined = values.employerEmail;

            if (values.action === 'APPROVE') {
                if (requiresMemberSignedDeductionFormUpload && isDeductionFormReview && !hasSignedDeductionForm) {
                    showErrorNotification('Please upload the signed deduction form before approving.');
                    return;
                }
                if (!selectedEmployerId && !(newEmployerName && newEmployerName.trim())) {
                    showErrorNotification('Please select an employer or create a new employer before approving.');
                    return;
                }

                if (selectedEmployerId) {
                    const employer = employerById[selectedEmployerId];
                    const nextAddress = employerAddressOverride?.trim();
                    const currentAddress = employer?.address?.trim();

                    const nextPoBox = employerPoBoxOverride?.trim();
                    const currentPoBox = employer?.poBox?.trim();

                    const nextPhone = employerPhoneOverride?.trim();
                    const currentPhone = employer?.phone?.trim();

                    const nextEmail = employerEmailOverride?.trim();
                    const currentEmail = employer?.email?.trim();

                    const hasEmployerUpdates = !!employer && (
                        (nextAddress || '') !== (currentAddress || '')
                        || (nextPoBox || '') !== (currentPoBox || '')
                        || (nextPhone || '') !== (currentPhone || '')
                        || (nextEmail || '') !== (currentEmail || '')
                    );

                    if (nextAddress && hasEmployerUpdates) {
                        await AXIOS_INSTANCE.put(`/frontend/v1/employers/${selectedEmployerId}`, {
                            name: employer.name,
                            voteNumber: employer.voteNumber,
                            address: nextAddress,
                            poBox: nextPoBox ? nextPoBox : null,
                            phone: nextPhone ? nextPhone : null,
                            email: nextEmail ? nextEmail : null
                        });
                    }
                }
            }
            
            if (values.action === 'APPROVE') {
                await approveSession({
                    sessionToken: application.sessionToken || '',
                    // @ts-ignore
                    officerId: userId,
                    comments: values.comments,
                    monthlyDeductionAmount,
                    employerId: selectedEmployerId,
                    newEmployerName,
                    newEmployerVoteNumber,
                    newEmployerAddress,
                    newEmployerPoBox,
                    newEmployerPhone,
                    newEmployerEmail,
                    officerInitials,
                    officerName,
                    officerPhone
                });
                showSuccessNotification('Application approved successfully');
            } else if (values.action === 'REJECT') {
                await rejectSession({
                    sessionToken: application.sessionToken || '',
                    // @ts-ignore
                    officerId: userId,
                    rejectionReason: values.rejectionReason || values.comments,
                    // @ts-ignore
                    comments: values.comments
                });
                showSuccessNotification('Application rejected successfully');
            }
            
            // Navigate back to the list
            navigate('/registration/review');
            
        } catch (error: any) {
            showErrorNotification(error.response?.data?.message || 'Failed to review application');
        } finally {
            setIsReviewing(false);
        }
    };

    const handleViewDocument = (document: any) => {
        if (!application?.sessionToken) return;
        
        // Open document in new tab for viewing
        const viewUrl = `/frontend/v1/self-registration/document/${application.sessionToken}/${document.id}?download=false`;
        window.open(viewUrl, '_blank');
    };

    const handleDownloadDocument = (document: any) => {
        if (!application?.sessionToken) return;
        
        // Download document
        const downloadUrl = `/frontend/v1/self-registration/document/${application.sessionToken}/${document.id}?download=true`;
        window.open(downloadUrl, '_blank');
    };

    const handleViewDepositEvidence = () => {
        if (!application?.sessionToken || !application?.directDepositProofRef) return;
        
        // Open deposit evidence document in new tab for viewing using the dedicated endpoint
        const viewUrl = `/frontend/v1/self-registration/deposit-evidence/${application.sessionToken}?download=false`;
        window.open(viewUrl, '_blank');
    };

    const handleDownloadDepositEvidence = () => {
        if (!application?.sessionToken || !application?.directDepositProofRef) return;
        
        // Download deposit evidence document using the dedicated endpoint
        const downloadUrl = `/frontend/v1/self-registration/deposit-evidence/${application.sessionToken}?download=true`;
        window.open(downloadUrl, '_blank');
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'PENDING_OFFICER_REVIEW': return 'orange';
            case 'OFFICER_APPROVED': return 'green';
            case 'REJECTED': return 'red';
            case 'COMPLETED': return 'green';
            case 'ACTIVE': return 'blue';
            default: return 'blue';
        }
    };

    const parseLocationData = (locationDataJson: string) => {
        try {
            return JSON.parse(locationDataJson);
        } catch {
            return null;
        }
    };

    const renderLocationInfo = (locationData: any) => {
        if (!locationData) return null;
        
        const { locationSelections, houseNumber, postalCode, addressType } = locationData;
        
        return (
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
                {/* Address Type */}
                {addressType && (
                    <div>
                        <Text strong>Address Type: </Text>
                        <Tag color="blue">{addressType.replace('_', ' ')}</Tag>
                    </div>
                )}
                
                {/* Zone Information */}
                {locationSelections?.zone && (
                    <div>
                        <Text strong>Zone: </Text>
                        <Text>{locationSelections.zone.name}</Text>
                    </div>
                )}
                
                {/* Address Details */}
                <div>
                    <Text strong>Address: </Text>
                    <Text>
                        {[
                            houseNumber ? `House: ${houseNumber}` : null,
                            locationSelections?.ward?.name ? `Ward: ${locationSelections.ward.name}` : null,
                            locationSelections?.district?.name ? `District: ${locationSelections.district.name}` : null,
                            locationSelections?.region?.name ? `Region: ${locationSelections.region.name}` : null,
                            postalCode ? `Postal: ${postalCode}` : null
                        ].filter(Boolean).join(', ') || 'Not fully specified'}
                    </Text>
                </div>
            </Space>
        );
    };

    if (loading) {
        return (
            <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                minHeight: '400px' 
            }}>
                <Spin size="large" />
            </div>
        );
    }

    if (!application) {
        return (
            <Result
                status="404"
                title="Application Not Found"
                subTitle="The requested application could not be found."
                extra={
                    <Button type="primary" onClick={() => navigate('/registration/review')}>
                        Back to Applications
                    </Button>
                }
            />
        );
    }

    return (
        <div style={{ padding: isMobile ? '16px' : '24px', maxWidth: '1200px', margin: '0 auto' }}>
            {/* Breadcrumb Navigation */}
            <Breadcrumb style={{ marginBottom: '20px' }}>
                <Breadcrumb.Item>
                    <Button 
                        type="link" 
                        icon={<ArrowLeftOutlined />} 
                        onClick={() => navigate('/registration/review')}
                        style={{ padding: 0 }}
                    >
                        Officer Review
                    </Button>
                </Breadcrumb.Item>
                <Breadcrumb.Item>Application Details</Breadcrumb.Item>
            </Breadcrumb>

            {/* Header */}
            <Card style={{ marginBottom: '20px' }}>
                <Row gutter={[16, 16]} align="middle">
                    <Col xs={24} sm={16}>
                        <Space direction="vertical" size="small">
                            <Title level={3} style={{ margin: 0 }}>
                                <Avatar icon={<UserOutlined />} style={{ marginRight: '12px' }} />
                                {application.fullName}
                            </Title>
                            <Space wrap>
                                <Tag icon={<PhoneOutlined />}>{application.phoneNumber}</Tag>
                                <Tag icon={<MailOutlined />}>{application.email}</Tag>
                                <Tag color={getStatusColor(application.status || '')}>
                                    {application.status?.replace('_', ' ')}
                                </Tag>
                            </Space>
                        </Space>
                    </Col>
                    <Col xs={24} sm={8} style={{ textAlign: isMobile ? 'left' : 'right' }}>
                        <Space direction="vertical" size="small">
                            <Text type="secondary">
                                <CalendarOutlined /> Submitted: {new Date(application.createdAt || '').toLocaleDateString()}
                            </Text>
                            <Text type="secondary">
                                <FileTextOutlined /> Documents: {application.uploadedDocuments?.length || 0}
                            </Text>
                        </Space>
                    </Col>
                </Row>
            </Card>

            <Row gutter={[20, 20]}>
                {/* Left Column - Application Details */}
                <Col xs={24} lg={14}>
                    {/* Personal Information */}
                    <Card title="Personal Information" style={{ marginBottom: '20px' }}>
                        <Descriptions column={isMobile ? 1 : 2} size="small">
                            <Descriptions.Item label="Full Name">
                                {application.fullName}
                            </Descriptions.Item>
                            <Descriptions.Item label="Phone Number">
                                {application.phoneNumber}
                            </Descriptions.Item>
                            <Descriptions.Item label="Email">
                                {application.email}
                            </Descriptions.Item>
                            <Descriptions.Item label="Date of Birth">
                                {application.dateOfBirth || 'Not provided'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Gender">
                                {application.gender || 'Not provided'}
                            </Descriptions.Item>
                            <Descriptions.Item label="National ID">
                                {application.nationalId || 'Not provided'}
                            </Descriptions.Item>
                        </Descriptions>
                    </Card>

                    {/* Employment Information */}
                    <Card title="Employment Information" style={{ marginBottom: '20px' }}>
                        {application.employerName && !application.employerId && !selectedEmployerId && (
                            <Alert
                                message="Employer Not Linked"
                                description={
                                    <>
                                        The applicant typed <Text strong>"{application.employerName}"</Text> as their employer name.
                                        Please search and select the correct employer below, or create a new one during approval.
                                    </>
                                }
                                type="warning"
                                showIcon
                                style={{ marginBottom: 16 }}
                            />
                        )}
                        <Descriptions column={isMobile ? 1 : 2} size="small">
                            <Descriptions.Item label="Employment Type">
                                {application.governmentEmployee ? 'Government' : 'Private'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Employer">
                                <Space>
                                    {(selectedEmployer?.name || application.employerName) || 'Not provided'}
                                    {application.employerId ? (
                                        <Tag color="green">Linked</Tag>
                                    ) : application.employerName ? (
                                        <Tag color="orange">Manual Entry</Tag>
                                    ) : null}
                                </Space>
                            </Descriptions.Item>
                            <Descriptions.Item label="Gross Salary">
                                {application.grossSalary ? `TZS ${application.grossSalary.toLocaleString()}` : 'Not provided'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Net Salary">
                                {application.netSalary ? `TZS ${application.netSalary.toLocaleString()}` : 'Not provided'}
                            </Descriptions.Item>
                        </Descriptions>
                    </Card>

                    {/* Location Information */}
                    <Card title={<><EnvironmentOutlined /> Location Information</>}>
                        {application.locationDataJson ? 
                            renderLocationInfo(parseLocationData(application.locationDataJson)) : 
                            <Text type="secondary">No location information provided</Text>
                        }
                    </Card>

                    {/* Direct Deposit Evidence */}
                    {application.directDepositPendingReview && (
                        <Card 
                            title={<><BankOutlined /> Direct Deposit Evidence</>} 
                            style={{ marginBottom: '20px' }}
                        >
                            <Alert
                                message="Direct Deposit Evidence Submitted"
                                description="This application includes direct deposit evidence that requires review"
                                type="warning"
                                showIcon
                                style={{ marginBottom: '16px' }}
                            />
                            
                            <Descriptions column={isMobile ? 1 : 2} size="small">
                                <Descriptions.Item label="Deposit Amount">
                                    <Text strong>
                                        <DollarOutlined /> TZS {application.directDepositAmount?.toLocaleString()}
                                    </Text>
                                </Descriptions.Item>
                                <Descriptions.Item label="Payment Date">
                                    {application.directDepositPaymentDate ? 
                                        new Date(application.directDepositPaymentDate).toLocaleDateString() : 
                                        'Not specified'
                                    }
                                </Descriptions.Item>
                                <Descriptions.Item label="Proof Reference" span={2}>
                                    <Space direction="vertical" style={{ width: '100%' }}>
                                        <Space>
                                            <Text>{application.directDepositProofRef ?'Receipt / Payment Notification ' : 'Not provided'}</Text>
                                            {application.directDepositProofRef && (
                                                <Space>
                                                    <Button 
                                                        type="link" 
                                                        icon={<EyeOutlined />}
                                                        onClick={() => handleViewDepositEvidence()}
                                                        size="small"
                                                    >
                                                        View
                                                    </Button>
                                                    <Button 
                                                        type="link" 
                                                        icon={<DownloadOutlined />}
                                                        onClick={() => handleDownloadDepositEvidence()}
                                                        size="small"
                                                    >
                                                        Download
                                                    </Button>
                                                </Space>
                                            )}
                                        </Space>
                                    </Space>
                                </Descriptions.Item>
                            </Descriptions>

                            <Divider orientation="left">Official Deduction Form</Divider>

                            <Alert
                                message="Officer must upload the official deduction form"
                                description={requiresMemberSignedDeductionFormUpload
                                    ? 'Upload the official/blank deduction form that the applicant will download, sign, and re-upload. Approval is blocked until this is uploaded.'
                                    : 'Upload the official/blank deduction form that the applicant will download and sign. Once uploaded, officer approval can finalize onboarding without waiting for the member to upload the signed copy back.'}
                                type={hasOfficialDeductionForm ? 'success' : 'warning'}
                                showIcon
                                style={{ marginBottom: 12 }}
                            />

                            <Upload
                                fileList={officialDeductionFormFileList}
                                beforeUpload={() => false}
                                maxCount={1}
                                onChange={({ fileList }) => setOfficialDeductionFormFileList(fileList)}
                                accept=".pdf,.png,.jpg,.jpeg"
                            >
                                <Button icon={<UploadOutlined />}>Select Official Deduction Form</Button>
                            </Upload>

                            <Button
                                type="primary"
                                onClick={handleUploadOfficialDeductionForm}
                                loading={isUploadingDocument}
                                disabled={officialDeductionFormFileList.length === 0}
                                style={{ marginTop: 8 }}
                            >
                                Upload Official Deduction Form
                            </Button>
                        </Card>
                    )}

                    {/* Documents */}
                    <Card title={<><FileTextOutlined /> Uploaded Documents</>}>
                        <List
                            dataSource={application.uploadedDocuments || []}
                            locale={{ emptyText: 'No documents uploaded' }}
                            renderItem={(doc) => (
                                <List.Item
                                    actions={[
                                        <Button 
                                            type="text" 
                                            icon={<EyeOutlined />}
                                            onClick={() => handleViewDocument(doc)}
                                            size="small"
                                        >
                                            View
                                        </Button>,
                                        <Button 
                                            type="text" 
                                            icon={<DownloadOutlined />}
                                            onClick={() => handleDownloadDocument(doc)}
                                            size="small"
                                        >
                                            Download
                                        </Button>
                                    ]}
                                >
                                    <List.Item.Meta
                                        avatar={<Avatar icon={<FileTextOutlined />} />}
                                        title={
                                            <Space>
                                                <Text strong>{(doc as any).documentType?.replace('_', ' ') || 'Unknown Document'}</Text>
                                                <Tag color={(doc as any).status === 'VALIDATED' ? 'green' : 'orange'}>
                                                    {(doc as any).status}
                                                </Tag>
                                            </Space>
                                        }
                                        description={
                                            <Space direction="vertical" size="small">
                                                <Text type="secondary">
                                                    {(doc as any).originalFileName || 'Unknown filename'}
                                                </Text>
                                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                                    Size: {(doc as any).fileSize ? ((doc as any).fileSize / 1024).toFixed(1) : 'Unknown'}KB | 
                                                    Type: {(doc as any).mimeType || 'Unknown'}
                                                </Text>
                                            </Space>
                                        }
                                    />
                                </List.Item>
                            )}
                        />
                    </Card>
                </Col>

                {/* Right Column - Review Actions */}
                <Col xs={24} lg={10}>
                    <Card title="Review Application" style={{ position: 'sticky', top: '20px' }}>
                        <Alert
                            message={canReviewApplication ? `Reviewing application for ${application.fullName}` : `Viewing application for ${application.fullName}`}
                            description={canReviewApplication
                                ? `Phone: ${application.phoneNumber} | Email: ${application.email}`
                                : `This record is in ${application.status?.replace(/_/g, ' ')} status and is available for viewing only.`}
                            type={canReviewApplication ? 'info' : 'warning'}
                            showIcon
                            style={{ marginBottom: '20px' }}
                        />

                        {canReviewApplication ? (
                        <Form
                            form={reviewForm}
                            layout="vertical"
                            onFinish={handleSubmitReview}
                        >
                            <Form.Item name="newEmployerName" hidden>
                                <Input />
                            </Form.Item>
                            <Form.Item name="newEmployerVoteNumber" hidden>
                                <Input />
                            </Form.Item>
                            <Form.Item name="newEmployerAddress" hidden>
                                <Input />
                            </Form.Item>
                            <Form.Item name="newEmployerPoBox" hidden>
                                <Input />
                            </Form.Item>
                            <Form.Item name="newEmployerPhone" hidden>
                                <Input />
                            </Form.Item>
                            <Form.Item name="newEmployerEmail" hidden>
                                <Input />
                            </Form.Item>

                            <Form.Item name="employerPoBox" hidden>
                                <Input />
                            </Form.Item>
                            <Form.Item name="employerPhone" hidden>
                                <Input />
                            </Form.Item>
                            <Form.Item name="employerEmail" hidden>
                                <Input />
                            </Form.Item>

                            <Form.Item
                                name="action"
                                label="Review Decision"
                                rules={[{ required: true, message: 'Please select a decision' }]}
                            >
                                <Select placeholder="Select decision" size="large">
                                    <Option value="APPROVE">
                                        <Space>
                                            <CheckOutlined style={{ color: '#52c41a' }} />
                                            Approve Application
                                        </Space>
                                    </Option>
                                    <Option value="REJECT">
                                        <Space>
                                            <CloseOutlined style={{ color: '#ff4d4f' }} />
                                            Reject Application
                                        </Space>
                                    </Option>
                                </Select>
                            </Form.Item>

                            <Form.Item
                                noStyle
                                shouldUpdate={(prevValues, currentValues) => prevValues.action !== currentValues.action}
                            >
                                {({ getFieldValue }) => {
                                    const action = getFieldValue('action');
                                    return action === 'APPROVE' ? (
                                        <>
                                            <Divider orientation="left">Employer {application.employerId ? 'Correction' : 'Mapping'}</Divider>

                                            {application.employerName && !selectedEmployerId && (
                                                <Alert
                                                    message={<>Applicant entered: <Text strong>"{application.employerName}"</Text></>}
                                                    description="Search for the matching employer below, or create a new one."
                                                    type="info"
                                                    showIcon
                                                    style={{ marginBottom: 12 }}
                                                />
                                            )}

                                            <Form.Item label="Employer (Search & Select)">
                                                <Select
                                                    showSearch
                                                    allowClear
                                                    placeholder="Type employer name..."
                                                    filterOption={false}
                                                    onSearch={(value) => setEmployerQuery(value)}
                                                    options={mergedEmployerOptions}
                                                    loading={isEmployerLoading}
                                                    value={selectedEmployerId}
                                                    onChange={(value) => {
                                                        void (async () => {
                                                            const nextEmployerId = value as number | undefined;
                                                            setSelectedEmployerId(nextEmployerId);
                                                            if (!nextEmployerId) {
                                                                reviewForm.setFieldsValue({
                                                                    employerAddress: undefined,
                                                                    employerPoBox: undefined,
                                                                    employerPhone: undefined,
                                                                    employerEmail: undefined,
                                                                    voteNumber: undefined
                                                                });
                                                                return;
                                                            }

                                                            try {
                                                                await refreshSelectedEmployer(nextEmployerId);
                                                            } catch (e) {
                                                                const employer = employerById[nextEmployerId];
                                                                reviewForm.setFieldsValue({
                                                                    employerAddress: employer?.address,
                                                                    employerPoBox: employer?.poBox,
                                                                    employerPhone: employer?.phone,
                                                                    employerEmail: employer?.email
                                                                });
                                                            }
                                                        })();
                                                    }}
                                                    size="large"
                                                />
                                            </Form.Item>

                                            {selectedEmployerId && selectedEmployer && (
                                                <Form.Item label="Vote Number">
                                                    <Input value={selectedEmployer.voteNumber || '-'} disabled />
                                                </Form.Item>
                                            )}

                                            <Form.Item name="employerAddress" label="Employer Address (for form)" rules={[{ required: true, message: 'Employer address is required for deduction form' }]}>
                                                <TextArea rows={2} placeholder="Auto-filled when employer is selected" disabled />
                                            </Form.Item>

                                            <Button
                                                icon={<EditOutlined />}
                                                block
                                                disabled={!selectedEmployerId}
                                                onClick={() => {
                                                    setEmployerEditModalOpen(true);
                                                }}
                                                style={{ marginBottom: 12 }}
                                            >
                                                Edit Employer Information
                                            </Button>

                                            <Button type="dashed" block onClick={() => setCreateEmployerModalOpen(true)}>
                                                Create New Employer
                                            </Button>

                                            <Divider orientation="left">Deduction Details</Divider>

                                            <Form.Item
                                                name="monthlyDeductionAmount"
                                                label="Monthly Deduction Amount"
                                                rules={[{ required: true, message: 'Monthly deduction amount is required' }]}
                                            >
                                                <InputNumber
                                                    style={{ width: '100%' }}
                                                    min={0}
                                                    step={1000}
                                                    placeholder="Enter monthly deduction amount"
                                                    size="large"
                                                />
                                            </Form.Item>

                                            <Space style={{ width: '100%', marginBottom: 12 }}>
                                                <Button
                                                    icon={<EyeOutlined />}
                                                    onClick={() => setDeductionPreviewOpen(true)}
                                                >
                                                    Preview Deduction Form
                                                </Button>
                                                <Button
                                                    icon={<PrinterOutlined />}
                                                    onClick={handlePrintDeductionForm}
                                                    loading={isPrinting}
                                                    disabled={isCompanyLoading || !companyData}
                                                >
                                                    Print
                                                </Button>
                                            </Space>

                                            <Divider orientation="left">Officer Signature</Divider>

                                            <Form.Item name="officerInitials" label="Officer Initials" rules={[{ required: true, message: 'Officer initials are required' }]}>
                                                <Input placeholder="Enter initials" size="large" />
                                            </Form.Item>

                                            <Form.Item name="officerName" label="Officer Name" rules={[{ required: true, message: 'Officer name is required' }]}>
                                                <Input placeholder="Enter officer name" size="large" />
                                            </Form.Item>

                                            <Form.Item name="officerPhone" label="Officer Phone" rules={[{ required: true, message: 'Officer phone is required' }]}>
                                                <Input placeholder="Enter officer phone" size="large" />
                                            </Form.Item>
                                        </>
                                    ) : null;
                                }}
                            </Form.Item>

                            <Form.Item
                                noStyle
                                shouldUpdate={(prevValues, currentValues) => prevValues.action !== currentValues.action}
                            >
                                {({ getFieldValue }) => {
                                    const action = getFieldValue('action');
                                    return action === 'REJECT' ? (
                                        <Form.Item
                                            name="rejectionReason"
                                            label="Rejection Reason"
                                            rules={[{ required: true, message: 'Please provide rejection reason' }]}
                                        >
                                            <Select placeholder="Select rejection reason" size="large">
                                                <Option value="INCOMPLETE_DOCUMENTS">Incomplete Documents</Option>
                                                <Option value="INVALID_INFORMATION">Invalid Information</Option>
                                                <Option value="ELIGIBILITY_CRITERIA">Does not meet eligibility criteria</Option>
                                                <Option value="DUPLICATE_APPLICATION">Duplicate Application</Option>
                                                <Option value="OTHER">Other</Option>
                                            </Select>
                                        </Form.Item>
                                    ) : null;
                                }}
                            </Form.Item>

                            <Form.Item
                                name="comments"
                                label="Comments"
                                rules={[{ required: true, message: 'Please provide comments' }]}
                            >
                                <TextArea
                                    rows={4}
                                    placeholder="Provide detailed comments about your decision..."
                                />
                            </Form.Item>

                            <Form.Item shouldUpdate>
                                {({ getFieldValue }) => {
                                    const action = getFieldValue('action');
                                    const disableSubmit = (action === 'APPROVE' && requiresMemberSignedDeductionFormUpload && isDeductionFormReview && !hasSignedDeductionForm)
                                        || (action === 'APPROVE' && isDepositEvidenceReview && !hasOfficialDeductionForm);
                                    return (
                                        <Space direction="vertical" style={{ width: '100%' }}>
                                            <Button
                                                type="primary"
                                                htmlType="submit"
                                                loading={isReviewing || isApprovingSession || isRejectingSession}
                                                size="large"
                                                block
                                                disabled={disableSubmit}
                                            >
                                                Submit Review
                                            </Button>
                                            <Button 
                                                onClick={() => navigate('/registration/review')}
                                                size="large"
                                                block
                                            >
                                                Cancel & Go Back
                                            </Button>
                                        </Space>
                                    );
                                }}
                            </Form.Item>
                        </Form>
                        ) : (
                            <Space direction="vertical" style={{ width: '100%' }} size="middle">
                                <Descriptions column={1} size="small" bordered>
                                    <Descriptions.Item label="Status">
                                        <Tag color={getStatusColor(application.status || '')}>
                                            {application.status?.replace(/_/g, ' ')}
                                        </Tag>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Current Step">
                                        {application.currentStep?.replace(/_/g, ' ') || 'N/A'}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Reviewed By">
                                        {application.reviewedBy || 'N/A'}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Reviewed At">
                                        {application.reviewedAt ? new Date(application.reviewedAt).toLocaleString() : 'N/A'}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Comments">
                                        {application.reviewComments || application.rejectionReason || 'No review notes available'}
                                    </Descriptions.Item>
                                </Descriptions>
                                <Button 
                                    onClick={() => navigate('/registration/review')}
                                    size="large"
                                    block
                                >
                                    Back to Applications
                                </Button>
                            </Space>
                        )}
                    </Card>
                </Col>
            </Row>

            <EmployerFormModal
                open={employerEditModalOpen}
                employer={selectedEmployerId ? (employerById[selectedEmployerId] as any) : null}
                onClose={() => setEmployerEditModalOpen(false)}
                onSaved={async () => {
                    if (selectedEmployerId) {
                        await refreshSelectedEmployer(selectedEmployerId);
                    }
                    setEmployerEditModalOpen(false);
                }}
            />

            <Modal
                title="Create New Employer"
                open={createEmployerModalOpen}
                onCancel={() => setCreateEmployerModalOpen(false)}
                footer={null}
                destroyOnClose
            >
                <Form
                    layout="vertical"
                    onFinish={(vals) => {
                        reviewForm.setFieldsValue({
                            newEmployerName: vals.name,
                            newEmployerVoteNumber: vals.voteNumber,
                            newEmployerAddress: vals.address,
                            newEmployerPoBox: vals.poBox,
                            newEmployerPhone: vals.phone,
                            newEmployerEmail: vals.email,
                            employerAddress: vals.address
                        });
                        setSelectedEmployerId(undefined);
                        setCreateEmployerModalOpen(false);
                    }}
                >
                    <Form.Item name="name" label="Employer Name" rules={[{ required: true, message: 'Employer name is required' }]}>
                        <Input placeholder="Employer name" />
                    </Form.Item>
                    <Form.Item name="voteNumber" label="Vote Number"
                               rules={[{ required: true, message: 'Please enter Vote Number' }]}>
                        <Input placeholder="Vote number" />
                    </Form.Item>
                    <Form.Item name="address" label="Employer Address" rules={[{ required: true, message: 'Employer address is required' }]}>
                        <TextArea rows={3} placeholder="Employer address" />
                    </Form.Item>
                    <Form.Item name="poBox" label="P.O. Box">
                        <Input placeholder="P.O. Box" />
                    </Form.Item>
                    <Form.Item name="phone" label="Phone">
                        <Input placeholder="Phone" />
                    </Form.Item>
                    <Form.Item name="email" label="Email">
                        <Input placeholder="Email" />
                    </Form.Item>
                    <Space style={{ width: '100%' }}>
                        <Button onClick={() => setCreateEmployerModalOpen(false)} style={{ flex: 1 }}>
                            Cancel
                        </Button>
                        <Button type="primary" htmlType="submit" style={{ flex: 1 }}>
                            Use Employer
                        </Button>
                    </Space>
                </Form>
            </Modal>

            <Modal
                title="Deduction Form Preview"
                open={deductionPreviewOpen}
                onCancel={() => setDeductionPreviewOpen(false)}
                width={900}
                footer={[
                    <Button key="close" onClick={() => setDeductionPreviewOpen(false)}>
                        Close
                    </Button>,
                    <Button
                        key="print"
                        type="primary"
                        icon={<PrinterOutlined />}
                        onClick={handlePrintDeductionForm}
                        loading={isPrinting}
                        disabled={isCompanyLoading || !companyData}
                    >
                        Print
                    </Button>
                ]}
            >
                <div style={{ display: 'none' }}>
                    <div ref={printableFormRef}>
                        {companyData && (
                            <SelfRegistrationDeductionForm
                                session={{
                                    ...(application as any),
                                    monthlyDeductionAmount: reviewForm.getFieldValue('monthlyDeductionAmount'),
                                    employerName: selectedEmployer?.name || application.employerName,
                                    employerAddress: reviewForm.getFieldValue('employerAddress'),
                                    voteNumber: resolvedVoteNumber,
                                    officerInitials: reviewForm.getFieldValue('officerInitials'),
                                    officerName: reviewForm.getFieldValue('officerName'),
                                    officerPhone: reviewForm.getFieldValue('officerPhone')
                                } as any}
                                company={companyData as any}
                                deductionFormConfig={deductionFormConfig}
                            />
                        )}
                    </div>
                </div>

                {companyData ? (
                    <SelfRegistrationDeductionForm
                        session={{
                            ...(application as any),
                            monthlyDeductionAmount: reviewForm.getFieldValue('monthlyDeductionAmount'),
                            employerName: selectedEmployer?.name || application.employerName,
                            employerAddress: reviewForm.getFieldValue('employerAddress'),
                            voteNumber: resolvedVoteNumber,
                            officerInitials: reviewForm.getFieldValue('officerInitials'),
                            officerName: reviewForm.getFieldValue('officerName'),
                            officerPhone: reviewForm.getFieldValue('officerPhone')
                        } as any}
                        company={companyData as any}
                        deductionFormConfig={deductionFormConfig}
                    />
                ) : (
                    <Spin />
                )}
            </Modal>
        </div>
    );
};

export default OfficerReviewDetailView;
